import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Loader2, FolderCheck, Link2, FileText, StickyNote, Zap } from "lucide-react";

const TYPE_META = {
  auto_check: { icon: Zap, label: "Auto check" },
  link: { icon: Link2, label: "Link" },
  policy: { icon: FileText, label: "Policy" },
  note: { icon: StickyNote, label: "Note" },
};

function AddEvidenceModal({ open, onClose, onSaved }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [type, setType] = useState("note");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast({ title: "Name is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/evidence", { name, type });
      toast({ title: "Evidence added" });
      setName("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't add evidence", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-evidence-modal">
        <DialogHeader><DialogTitle>Add evidence</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus data-testid="compliance-evidence-name-input" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1" data-testid="compliance-evidence-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="link">Link</SelectItem>
                <SelectItem value="policy">Policy</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-evidence-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-evidence-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceEvidence({ isAdmin }) {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/evidence");
      setEvidence(data.evidence || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-3" data-testid="compliance-evidence-page">
      {isAdmin && (
        <div className="flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setModalOpen(true)} data-testid="compliance-evidence-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add evidence
          </Button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="compliance-evidence-grid">
        {evidence.map((e) => {
          const meta = TYPE_META[e.type] || TYPE_META.note;
          return (
            <div key={e.id} className="rounded-xl border border-border bg-card p-3.5" data-testid={`compliance-evidence-card-${e.id}`}>
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                  <meta.icon className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="text-[12.5px] font-medium leading-snug">{e.name}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5">
                    <span className="uppercase">{e.source}</span>
                    <span>·</span>
                    <span>{e.collected_at}</span>
                  </div>
                </div>
              </div>
              <span className={cn(
                "inline-block mt-2.5 text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5",
                e.status === "valid" && "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
                e.status === "expiring_soon" && "border-amber-500/40 bg-amber-500/10 text-amber-600",
                e.status === "expired" && "border-red-500/40 bg-red-500/10 text-red-600"
              )}>{{ valid: "Valid", expiring_soon: "Expiring soon", expired: "Expired" }[e.status] || e.status}</span>
            </div>
          );
        })}
        {evidence.length === 0 && (
          <div className="col-span-full py-10 text-center text-[12.5px] text-muted-foreground flex flex-col items-center gap-2">
            <FolderCheck className="w-5 h-5" /> No evidence collected yet.
          </div>
        )}
      </div>
      <AddEvidenceModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
    </div>
  );
}
